import { ColumnsProps, useWindowResize } from '@/Helper';
import { NonActionHeaderColumn } from '@/components/common/VirtualTable/NonActionHeaderColumn';
import { RefWrap } from '@/components/common/VirtualTable/RefWrap';
import { EventPaginationSetting } from '@/model/AlarmModel';
import { SelectOptions } from '@/model/CommonModel';
import { StatusIcon } from '@/resources/Icons';
import cx from 'classnames';
import memoize from 'memoize-one';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import { AlarmActionColumnData, AlarmTableDataSourceModel, getAlarmCount, getNextPage } from '../helpers';
import { columnKeys, getAlarmActionColumnDataObject } from './ExpandableRow';
import { Row } from './Row';
import styles from './virtualTableExpandableRow.module.scss';

const minimumRowHeight: number = 31;
type Props = {
	headerColumnsWidth: CSSProperties;
	columns: ColumnsProps<AlarmTableDataSourceModel>[];
	dataSource: AlarmTableDataSourceModel[];
	pageSize: number;
	maxHeight: number;
	idTable: string;
	alwaysShowInstructions: boolean;
	alarmStackingEnabled: boolean;
	totalAlarms: number;
	containerClassName: string;
	fetchData: (paginationSettings: EventPaginationSetting) => void;
	handleOnExpandRow: (itemIndex: number) => void;
	sessionStorageFetchKey: string;
	sessionStorageLiveEventKey: string;
	customStyles?: CSSProperties;
	contextMenuOptions?: SelectOptions<number>[];
	onClickContextMenuOption?: (key: number) => void;
	onRightClickRow?: (checked: boolean, activeAlarmId: number, isExpandableRowHeader: boolean, isExpanded: boolean) => void;
	paginationSettings: EventPaginationSetting;
	onSort: (paginationSettings: EventPaginationSetting) => void;
};

type sizeMap = {
	[index: number]: number;
};

type MemoizedDataType = {
	items: AlarmTableDataSourceModel[];
	toggleItemActive: (itemIndex: number) => void;
};

const createItem = memoize(
	(items, toggleItemActive): MemoizedDataType => ({
		items,
		toggleItemActive,
	})
);

const component = ({
	columns,
	dataSource,
	pageSize,
	headerColumnsWidth,
	maxHeight,
	idTable,
	alarmStackingEnabled,
	alwaysShowInstructions,
	totalAlarms,
	containerClassName,
	fetchData,
	handleOnExpandRow,
	sessionStorageFetchKey,
	sessionStorageLiveEventKey,
	customStyles,
	onRightClickRow,
	contextMenuOptions,
	onClickContextMenuOption,
	paginationSettings,
	onSort,
}: Props) => {
	//#region State Management Variables
	const [headerScroll, setHeaderScroll] = useState<number>(0);
	const [height, setHeight] = useState<number>(minimumRowHeight);

	const listRef = useRef(undefined);
	const headerRef = useRef(undefined);
	const sizeMap = useRef<sizeMap>({});

	const setSize = useCallback((index: number, size: number) => {
		sizeMap.current = { ...sizeMap.current, [index]: size };
		if (listRef?.current) {
			listRef.current.resetAfterIndex(index);
		}
	}, []);

	const [windowWidth] = useWindowResize();
	//#endregion

	useEffect(() => {
		if (headerRef?.current) {
			headerRef.current.scrollLeft = headerScroll;
		}
	}, [headerScroll]);

	useEffect(() => {
		if (listRef?.current && dataSource?.length > 0) {
			listRef.current.scrollToItem(0, 'start');
		}
	}, [height]);

	const getSize = (index: number): number => {
		let size: number = sizeMap.current[index] || minimumRowHeight;
		if (index === dataSource.length - 1) {
			const containerElement: HTMLElement = document.getElementsByClassName(containerClassName)[0] as HTMLElement;
			const horizontalScrollPixels: number = containerElement?.scrollWidth > containerElement?.clientWidth ? 16 : 0;
			size += horizontalScrollPixels;
		}
		return size;
	};

	const onFetchData = (): void => {
		if (window.sessionStorage.getItem(sessionStorageFetchKey) === '1' || window.sessionStorage.getItem(sessionStorageLiveEventKey) === '1') {
			return;
		}

		const quantityOfAlarms = getAlarmCount(dataSource);
		if (quantityOfAlarms !== totalAlarms) {
			const newPage = getNextPage(quantityOfAlarms, pageSize);
			fetchData({
				...paginationSettings,
				PageNumber: newPage,
			});
		}
	};

	const handleInnerRef = ref => {
		if (ref?.children) {
			setTimeout(() => {
				let totalHeight: number = 0;
				for (let index = 0; index < ref.children.length; index++) {
					const elementDiv = ref.children[index];
					if (elementDiv && elementDiv.style.height) {
						const heightNumber: number = Number(elementDiv.style.height.toLowerCase().split('px')[0]);
						if (!isNaN(heightNumber) && heightNumber <= maxHeight && heightNumber >= 2) {
							totalHeight += heightNumber;
						} else {
							return;
						}
					}
				}

				if (totalHeight === 0) {
					setHeight(minimumRowHeight - 2);
				} else if (totalHeight <= maxHeight && totalHeight >= 2) {
					setHeight(totalHeight - 2);
				} else if (height !== maxHeight) {
					setHeight(maxHeight);
				}
			}, 0);
		}
	};

	const listKey: string = dataSource.length === 0 ? `${Math.floor(Math.random() * 100)}` : undefined;
	const itemData = createItem(dataSource, handleOnExpandRow);

	const listComponent: JSX.Element = (
		<RefWrap fetchData={onFetchData} headerScroll={headerScroll} setHeaderScroll={setHeaderScroll}>
			<List
				data-id={idTable}
				ref={listRef}
				height={height}
				width='100%'
				itemCount={dataSource.length}
				itemSize={getSize}
				itemData={itemData}
				innerRef={handleInnerRef}
				overscanCount={10}
				style={customStyles}
				className={cx(styles.list, containerClassName)}>
				{({ data, index, style }) => {
					const { items, toggleItemActive } = data;
					const dataRow = items as AlarmTableDataSourceModel[];
					const alarmData = dataRow[index];
					const isExpandableRow = (alarmData?.AlarmDetails.length > 1 && alarmStackingEnabled) || alarmData?.IsExpanded;

					return (
						<Row
							data={alarmData}
							index={index}
							setSize={setSize}
							windowWidth={windowWidth}
							columns={columns}
							rowColumnsWidth={headerColumnsWidth}
							alwaysShowInstructions={alwaysShowInstructions}
							alarmStackingEnabled={alarmStackingEnabled}
							handleOnToggleItemActive={toggleItemActive}
							isExpandableRow={isExpandableRow}
							contextMenuOptions={contextMenuOptions}
							onClickContextMenuOption={onClickContextMenuOption}
							onRightClickRow={onRightClickRow}
							style={style}
						/>
					);
				}}
			</List>
		</RefWrap>
	);

	return (
		<div className={styles.container}>
			<div ref={headerRef} className={styles.header} style={headerColumnsWidth}>
				{columns.map((column: ColumnsProps<AlarmTableDataSourceModel>, index: number) => {
					const hasSeparator: boolean = index > 2;
					if (column.key === 'CheckColumnHeader') {
						const alarmActionColumn: AlarmActionColumnData = {
							...getAlarmActionColumnDataObject(null, null),
							columnActionHeader: true,
							dataSource,
						};
						return (
							<div key={`header-${column.dataIndex}`} style={{ justifyContent: column.align }}>
								{column.render(alarmActionColumn, undefined, 0) as React.ReactNode}
							</div>
						);
					} else {
						const style: React.CSSProperties = {};
						if (column.dataIndex === columnKeys.ActionsColumn) {
							style.gridColumn = `${columns.length}/-1`;
						} else if (column.dataIndex === columnKeys.Icon) {
							const tooltip =
								column['data-header'] === 'Icon' || column['data-header'] === '<Icon>' || column['data-header'] === ''
									? 'Status'
									: column['data-header'];
							column.title = (
								<span title={tooltip?.toString()}>
									<StatusIcon />
								</span>
							);
							style.padding = '0px 0px 0px 2px';
						}

						return (
							<NonActionHeaderColumn
								column={column}
								separator={hasSeparator}
								key={`header-${column.dataIndex}`}
								onSort={onSort}
								paginationSettings={paginationSettings}
								style={style}
							/>
						);
					}
				})}
			</div>
			<React.Fragment key={listKey}>{listComponent}</React.Fragment>
		</div>
	);
};

const VirtualTableExpandableRow = React.memo(component);
VirtualTableExpandableRow.displayName = 'VirtualTableExpandableRow';

export { VirtualTableExpandableRow };
